import { Type } from "@sinclair/typebox";
import { ApiNotFoundError } from "@zuplo/errors";
import { Hono } from "hono";
import { streamSSE } from "hono/streaming";
import { editorConfig } from "../constants.js";
import type { CommonLogger, SseClient } from "../interfaces.js";
import type { EditorPreferencesPort, FileSystemPort } from "../ports.js";
import { validate } from "../validation.js";
import { createEditorRoutes } from "./editor.js";
import { createFsRoutes } from "./fs.js";
import { createTestRoutes } from "./test.js";

const POLICIES_FILENAME = "policies.json";

interface ApiRoutesOptions {
  /** Only used to build external-editor deep links — file access goes via `fs`. */
  sourceDirectory: string;
  fs: FileSystemPort;
  /** Distinct from the portal's own user preferences stubbed further down. */
  editorPreferences: EditorPreferencesPort;
  logger: CommonLogger;
  sseClients: Set<SseClient>;
  deploymentUrl: string;
}

const PreferencesBody = Type.Object({
  data: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
});

export function createApiRoutes(options: ApiRoutesOptions): Hono {
  const {
    sourceDirectory,
    fs,
    editorPreferences,
    logger,
    sseClients,
    deploymentUrl,
  } = options;
  const app = new Hono();

  // Filesystem routes
  app.route(
    "/v1/deployments/:deploymentName/fs",
    createFsRoutes({ fs, logger }),
  );

  // Editor routes (for opening files in external editor)
  app.route(
    "/v1/editor",
    createEditorRoutes({
      sourceDirectory,
      preferences: editorPreferences,
    }),
  );

  // Test routes (for proxying test requests to local Zuplo gateway). Mounted
  // twice because Fastify answered on both spellings and Hono doesn't: the
  // portal posts to /v1/test, but the real test-proxy accepts either.
  const testRoutes = createTestRoutes({ logger });
  app.route("/v1/test", testRoutes);
  app.route("/v1/test/", testRoutes);

  // Server-Sent Events for file changes
  app.get("/changes", (c) =>
    streamSSE(c, async (stream) => {
      // Resolved when the client disconnects or the server shuts the connection
      // down. Until then this handler stays suspended, which is what holds the
      // response open.
      let release!: () => void;
      const disconnected = new Promise<void>((resolve) => {
        release = resolve;
      });

      const client: SseClient = {
        send: (event, data) => stream.writeSSE({ event, data }),
        get closed() {
          return stream.closed || stream.aborted;
        },
        close: async () => release(),
      };

      stream.onAbort(release);
      sseClients.add(client);

      // Send initial event
      await stream.writeSSE({
        data: JSON.stringify({ type: "init", filePath: POLICIES_FILENAME }),
      });

      // Send keep-alive comments to prevent proxy/browser timeouts
      const keepAliveInterval = setInterval(() => {
        if (client.closed) {
          clearInterval(keepAliveInterval);
          release();
          return;
        }
        void stream.write(": keep-alive\n\n");
      }, editorConfig.sse.keepAliveInterval);

      try {
        await disconnected;
      } finally {
        clearInterval(keepAliveInterval);
        sseClients.delete(client);
        logger.debug("SSE client disconnected");
      }
    }),
  );

  // Health check
  app.get("/ping", (c) => c.body(null, 200));

  // User preferences stub. The portal Code tab reads file-tree open/collapsed
  // state from GET /users/me/preferences (and writes via PUT). Local mode has
  // no real user store — keep an in-memory copy for the process lifetime so
  // the editor can load and the expand/collapse toggle still works in-session.
  let userPreferences: Record<string, unknown> = {};

  app.get("/v1/users/me/preferences", (c) => c.json({ data: userPreferences }));

  app.put(
    "/v1/users/me/preferences",
    validate("json", PreferencesBody),
    (c) => {
      userPreferences = c.req.valid("json").data ?? {};
      return c.json({ data: userPreferences });
    },
  );

  // Get deployment info (includes URL for local gateway)
  app.get("/v1/deployments/:deploymentName", (c) =>
    c.json({
      deploymentVersion: {
        stages: [
          {
            stage: "api",
            url: deploymentUrl,
          },
        ],
      },
    }),
  );

  // Return empty versions list for local mode (no build history)
  app.get("/v1/deployments/:deploymentName/versions", (c) =>
    c.json({ data: [] }),
  );

  // Serve CDN content (schemas, etc) by proxying to the real Zuplo CDN
  // The frontend is configured with CDN_URL="/api" in local mode
  // So requests like /api/schemas/openapi-v3.1-zuplo.json need to be proxied
  app.get("/schemas/*", async (c) => {
    // Drop whatever prefix these routes are mounted under (`/api` in the
    // server, nothing in tests) to get the path as the CDN knows it.
    const cdnPath = c.req.path.slice(c.req.path.indexOf("/schemas/"));
    const cdnUrl = `https://cdn.zuplo.com${cdnPath}`;

    const response = await fetch(cdnUrl);
    if (!response.ok) {
      throw new ApiNotFoundError();
    }

    const contentType = response.headers.get("content-type");
    if (contentType) {
      c.header("Content-Type", contentType);
    }

    const data = await response.text();
    return c.body(data);
  });

  return app;
}
